import React from 'react'
import useSelector from 'hooks/use-selector'
import useDispatch from 'hooks/use-dispatch'
import { SIDEBAR_ID } from 'modules/sidebar/constants'
import openMiniCartSidebarGiftAction from 'modules/cart/redux/actions/open-mini-cart-sidebar'
import { GIFT_MESSAGE } from 'modules/gift/constants'
import getHashStringUtil from 'utils/string/get-hash'
import getHashFromUrlLocationUtil from 'utils/location/get-hash-value-from-url'
import updateCurrentToastAction from 'modules/toast/redux/actions/update-current'
import removeHashLocationUtil from 'utils/location/remove-hash'
import runAfterDelayTimingUtil from 'utils/timing/run-after-delay'
import openQuickShopSidebarProductAction from 'modules/product/redux/actions/open-quick-shop-product-sidebar'
import closeSidebarAction from 'modules/sidebar/redux/actions/close'
import getCurrentIdSidebarSelect from 'modules/sidebar/redux/selectors/get-current-id'
import updateHashLocationUtil from 'utils/location/update-hash'
import getPathnameWithQueryLocationUtil from 'utils/location/get-pathname-with-query'
import usePrevious from 'hooks/use-previous'
import pushStateHistoryUtil from 'utils/history/push-state'
import { CART_HASH_VALUE_ACTION } from 'modules/cart/constants'
import wasOpenedFromMiniCartProductSelect from 'modules/product/redux/selectors/was-opened-from-mini-cart'
import isProductUrlLocationUtil from 'utils/location/is-product-url'

let hasChangeListenerAdded = false

/**
 * @param props
 * @param props.quickShopData
 * @param props.onCreateCheckoutPermalink
 * @param props.onCreateExpressCheckoutPermalink
 * @return {JSX.Element}
 * @constructor
 */
export default function HandleHashChange (props) {
  const {
    quickShopData,
    onCreateCheckoutPermalink,
    onCreateExpressCheckoutPermalink
  } = props
  const dispatch = useDispatch()
  const sidebarId = useSelector(getCurrentIdSidebarSelect)
  const isSidebarOpen = !!sidebarId
  const sidebarIdRef = React.useRef(sidebarId)
  const prevIsSidebarOpen = usePrevious(isSidebarOpen)
  const wasOpenedFromMiniCart = useSelector(wasOpenedFromMiniCartProductSelect)

  // Add delay so MiniCartSidebar receives the `handleHashChange` event
  const removeHashAfterDelay = async () => {
    await runAfterDelayTimingUtil()
    removeHashLocationUtil()
  }

  const handleHashChange = () => {
    logUtil('HandleHashChange#handleHashChange').debug('handleHashChange', {
      currentSidebarId: sidebarIdRef.current
    })
    /**
     * If open from Mini Cart is true then stop calling this
     */
    if (wasOpenedFromMiniCart) {
      logUtil('HandleHashChange#handleHashChange').debug(`noop wasOpenedFromMiniCart=${wasOpenedFromMiniCart}`)
      return
    }

    const hash = getHashFromUrlLocationUtil()
    logUtil('HandleHashChange#handleHashChange').debug(`hash=${hash} props=`, props)

    // @see {@link GetFreeBonusDeluxeSamplesModalMounterContent}
    // @see {@link GetFreeBonusSamplesModalMounterContent}
    if (
      hash === getHashStringUtil(CART_HASH_VALUE_ACTION.SHOW_FREE_BONUS_DELUXE_SAMPLES_MODAL) ||
      hash === getHashStringUtil(CART_HASH_VALUE_ACTION.SHOW_FREE_BONUS_SAMPLES_MODAL)
    ) {
      logUtil('HandleHashChange#handleHashChange').debug(`hash=${hash} [noop]`)
      return
    }

    const isQuickShopProductSidebar = (
      hash === getHashStringUtil(CART_HASH_VALUE_ACTION.OPEN_QUICK_SHOP_PRODUCT_SIDEBAR)
    )

    if (hash === getHashStringUtil(CART_HASH_VALUE_ACTION.OPEN_MINI_CART_SIDEBAR)) {
      logUtil('HandleHashChange#handleHashChange').debug('call openMiniCartSidebarGiftAction')
      dispatch(openMiniCartSidebarGiftAction())

      return
    }

    if (hash === getHashStringUtil(CART_HASH_VALUE_ACTION.CREATE_EXPRESS_CHECKOUT_PERMALINK)) {
      removeHashLocationUtil()
      onCreateExpressCheckoutPermalink?.()
      return
    }

    if (hash === getHashStringUtil(CART_HASH_VALUE_ACTION.CREATE_CHECKOUT_PERMALINK)) {
      removeHashLocationUtil()
      onCreateCheckoutPermalink?.()
      return
    }

    if (
      isQuickShopProductSidebar &&
      sidebarIdRef.current !== SIDEBAR_ID.QUICK_SHOP_PRODUCT &&
      !isProductUrlLocationUtil() &&
      sidebarIdRef.current !== SIDEBAR_ID.SUBSCRIPTIONS
    ) {
      logUtil('HandleHashChange#handleHashChange').debug('call openQuickShopSidebarProductAction')
      dispatch(openQuickShopSidebarProductAction(quickShopData))
      return
    }

    if (isQuickShopProductSidebar) {
      return
    }

    if (hash === getHashStringUtil(CART_HASH_VALUE_ACTION.SHOW_ADD_SAMPLE_TOAST)) {
      dispatch(updateCurrentToastAction(GIFT_MESSAGE.YOU_MUST_ADD_A_SAMPLE))
      removeHashAfterDelay()
      return
    }

    if (hash === getHashStringUtil(CART_HASH_VALUE_ACTION.SHOW_STORE_CREDIT_TOAST)) {
      dispatch(updateCurrentToastAction(GIFT_MESSAGE.ADD_STORE_CREDIT_OR_A_FULL_SIZE_TO_CONTINUE))
      removeHashAfterDelay()
      return
    }

    if (hash === getHashStringUtil(CART_HASH_VALUE_ACTION.SHOW_CART_MINIMUM_TO_CHECKOUT)) {
      dispatch(updateCurrentToastAction(GIFT_MESSAGE.CART_MINIMUM_TO_CHECKOUT))
      removeHashAfterDelay()
      return
    }

    dispatch(closeSidebarAction())
  }

  const handleMount = async () => {
    if (!hasChangeListenerAdded) {
      window.addEventListener('hashchange', handleHashChange)
      hasChangeListenerAdded = true
    }

    // Add delay so the sidebar gets ready
    await runAfterDelayTimingUtil()
    handleHashChange()
  }

  const handleUnmount = () => {
    window.removeEventListener('hashchange', handleHashChange)
  }
  React.useEffect(() => {
    handleMount()
    return handleUnmount
  }, [])

  React.useEffect(() => {
    sidebarIdRef.current = sidebarId
  }, [sidebarId])

  React.useEffect(() => {
    if (!prevIsSidebarOpen) {
      return
    }

    if (
      sidebarId &&
      [
        SIDEBAR_ID.MINI_CART,
        SIDEBAR_ID.QUICK_SHOP_PRODUCT
      ].includes(sidebarId)
    ) {
      return
    }

    if (sidebarId === SIDEBAR_ID.MINI_CART) {
      updateHashLocationUtil(CART_HASH_VALUE_ACTION.OPEN_MINI_CART_SIDEBAR)
      return
    }

    if (sidebarId === SIDEBAR_ID.QUICK_SHOP_PRODUCT) {
      updateHashLocationUtil(CART_HASH_VALUE_ACTION.OPEN_QUICK_SHOP_PRODUCT_SIDEBAR)
      return
    }

    pushStateHistoryUtil(
      getPathnameWithQueryLocationUtil()
    )
  }, [isSidebarOpen])

  return (
    <></>
  )
}
